import type {
  AdRow,
  CombinedOrderReport,
  CompensationRow,
  CsvOrderRow,
  MeeshoCombinedReport,
  MeeshoPaymentFile,
  OrderRow,
  ReferralRow,
  SkuCostConfig,
  SkuReportItem
} from "@/features/profit-calculators/calculator-shared/types"

function getHigherPriorityStatus(status1: string, status2: string): string {
  if (!status1) return status2 || ""
  if (!status2) return status1
  const priority = ["EXCHANGE", "RETURN", "RTO", "DELIVERED", "SHIPPED", "CANCEL"]
  const getIndex = (s: string) => {
    const upper = s.toUpperCase()
    for (let i = 0; i < priority.length; i++) {
      if (upper.includes(priority[i])) return i
    }
    return priority.length
  }
  return getIndex(status1) <= getIndex(status2) ? status1 : status2
}

export function normalizeStatus(status: string): string {
  const s = status.toUpperCase().trim()
  if (s.includes("DELIVERED") || s.includes("SHIPPED")) return "DELIVERED"
  if (s.includes("CANCEL")) return "CANCELLED"
  if (s.includes("RTO COMPLETE") || s.includes("RTO_COMPLETE") || s === "RTO") return "RTO COMPLETE"
  if (s.includes("RTO LOCKED") || s.includes("RTO_LOCKED")) return "RTO LOCKED"
  if (s.includes("RETURN") || s.includes("RETURNED")) return "RETURNED"
  if (s.includes("EXCHANGE") || s.includes("EXCHANGED")) return "RETURNED"
  if (s.includes("CLAIM") || s.includes("CLAIMED") || s.includes("COMPENSATION")) return "CLAIMED"
  return s || "UNKNOWN"
}

function computeSingleMonthReport(
  monthLabel: string,
  csvOrders: CsvOrderRow[],
  paymentOrders: OrderRow[],
  ads: AdRow[],
  referrals: ReferralRow[],
  compensations: CompensationRow[],
  skuCosts: Record<string, SkuCostConfig>
): MeeshoCombinedReport {
  // Aggregate payment rows per subOrderNo
  const mergedPaymentOrders = new Map<string, OrderRow>()
  for (const order of paymentOrders) {
    const existing = mergedPaymentOrders.get(order.subOrderNo)
    if (existing) {
      existing.finalSettlementAmount += order.finalSettlementAmount
      existing.totalSaleAmount += order.totalSaleAmount
      existing.totalSaleReturnAmount += order.totalSaleReturnAmount
      existing.meeshoCommission += order.meeshoCommission
      existing.shippingCharge += order.shippingCharge
      existing.returnShippingCharge += order.returnShippingCharge
      existing.tcs += order.tcs
      existing.tds += order.tds
      existing.compensation += order.compensation
      existing.claims += order.claims
      existing.recovery += order.recovery
      if (order.liveOrderStatus) {
        existing.liveOrderStatus = existing.liveOrderStatus
          ? getHigherPriorityStatus(existing.liveOrderStatus, order.liveOrderStatus)
          : order.liveOrderStatus
      }
    } else {
      mergedPaymentOrders.set(order.subOrderNo, { ...order })
    }
  }

  const totalAdsCost = Math.abs(ads.reduce((s, a) => s + a.totalAdsCost, 0))
  const totalReferral = referrals.reduce((s, r) => s + r.netReferralAmount, 0)

  const combinedOrders: CombinedOrderReport[] = []
  let totalSettlement = 0
  let totalPurchaseCost = 0
  let totalUnits = 0

  const deliveredSplit = {
    notReturned: { count: 0, units: 0, settlement: 0 },
    returned: { count: 0, units: 0, settlement: 0 }
  }
  const undeliveredSplit = {
    cancelled: { count: 0, units: 0, settlement: 0 },
    claimed: { count: 0, units: 0, settlement: 0 },
    rtoComplete: { count: 0, units: 0, settlement: 0 },
    exchanged: { count: 0, units: 0, settlement: 0 },
    rtoLocked: { count: 0, units: 0, settlement: 0 }
  }

  const skuMetricsMap = new Map<
    string,
    {
      sku: string
      productName: string
      settlement: number
      purchaseCost: number
      unitsSold: number
      unitsReturned: number
      profit: number
    }
  >()

  for (const csvOrder of csvOrders) {
    const subNo = csvOrder.subOrderNo
    const matchedPayment = mergedPaymentOrders.get(subNo)
    const quantity = csvOrder.quantity || 1
    totalUnits += quantity

    const matchedStatus = matchedPayment?.liveOrderStatus || ""
    const csvStatus = csvOrder.reasonForCreditEntry || ""
    const claims = matchedPayment?.claims || 0
    const settlement = matchedPayment ? matchedPayment.finalSettlementAmount : 0

    let normStatus = "UNKNOWN"
    if (csvStatus.toUpperCase() === "CANCELLED") {
      normStatus = "CANCELLED"
    } else if (csvStatus.toUpperCase() === "RTO_LOCKED") {
      normStatus = "RTO LOCKED"
    } else if (csvStatus.toUpperCase() === "RTO_COMPLETE" || matchedStatus.toUpperCase().includes("RTO")) {
      normStatus = "RTO COMPLETE"
    } else if (matchedStatus.toUpperCase().includes("RETURN") && claims > 0 && settlement > 0) {
      normStatus = "CLAIMED"
    } else if (
      matchedStatus.toUpperCase().includes("RETURN") ||
      matchedStatus.toUpperCase().includes("EXCHANGE") ||
      csvStatus.toUpperCase() === "DOOR_STEP_EXCHANGED"
    ) {
      normStatus = "RETURNED"
    } else if (matchedStatus) {
      normStatus = normalizeStatus(matchedStatus)
    } else if (csvStatus) {
      normStatus = normalizeStatus(csvStatus)
    } else {
      normStatus = "DELIVERED"
    }

    totalSettlement += settlement

    const costConfig = skuCosts[csvOrder.sku]
    let orderCogs = 0
    if (costConfig) {
      const prodCostWithGst = costConfig.productPrice * (1 + costConfig.productGst / 100)
      const packCostWithGst = costConfig.packingPrice * (1 + costConfig.packingGst / 100)

      if (normStatus === "CANCELLED" || normStatus === "CLAIMED" || normStatus === "EXCHANGED") {
        orderCogs = 0
      } else if (normStatus === "RTO COMPLETE" || normStatus === "RTO LOCKED" || normStatus === "RETURNED") {
        orderCogs = packCostWithGst * quantity
      } else {
        orderCogs = (prodCostWithGst + packCostWithGst + costConfig.otherPrice) * quantity
      }
    }
    totalPurchaseCost += orderCogs

    const orderProfit = settlement - orderCogs

    combinedOrders.push({
      subOrderNo: subNo,
      orderDate: csvOrder.orderDate || matchedPayment?.orderDate || "",
      productName: csvOrder.productName || matchedPayment?.productName || "",
      sku: csvOrder.sku,
      liveOrderStatus: normStatus,
      quantity,
      settlementAmount: settlement,
      cogs: orderCogs,
      profit: orderProfit,
      originalOrderRow: matchedPayment
    })

    if (normStatus === "DELIVERED") {
      deliveredSplit.notReturned.count++
      deliveredSplit.notReturned.units += quantity
      deliveredSplit.notReturned.settlement += settlement
    } else if (normStatus === "RETURNED") {
      deliveredSplit.returned.count++
      deliveredSplit.returned.units += quantity
      deliveredSplit.returned.settlement += settlement
    } else if (normStatus === "CANCELLED") {
      undeliveredSplit.cancelled.count++
      undeliveredSplit.cancelled.units += quantity
      undeliveredSplit.cancelled.settlement += settlement
    } else if (normStatus === "CLAIMED") {
      undeliveredSplit.claimed.count++
      undeliveredSplit.claimed.units += quantity
      undeliveredSplit.claimed.settlement += settlement
    } else if (normStatus === "RTO COMPLETE") {
      undeliveredSplit.rtoComplete.count++
      undeliveredSplit.rtoComplete.units += quantity
      undeliveredSplit.rtoComplete.settlement += settlement
    } else if (normStatus === "EXCHANGED") {
      undeliveredSplit.exchanged.count++
      undeliveredSplit.exchanged.units += quantity
      undeliveredSplit.exchanged.settlement += settlement
    } else if (normStatus === "RTO LOCKED") {
      undeliveredSplit.rtoLocked.count++
      undeliveredSplit.rtoLocked.units += quantity
      undeliveredSplit.rtoLocked.settlement += settlement
    }

    if (normStatus === "DELIVERED" || normStatus === "RETURNED") {
      const skuKey = csvOrder.sku || "UNKNOWN"
      const existingSku = skuMetricsMap.get(skuKey) ?? {
        sku: skuKey,
        productName: csvOrder.productName || matchedPayment?.productName || "Unknown Product",
        settlement: 0,
        purchaseCost: 0,
        unitsSold: 0,
        unitsReturned: 0,
        profit: 0
      }
      if (normStatus === "DELIVERED") {
        existingSku.settlement += settlement
        existingSku.purchaseCost += orderCogs
        existingSku.unitsSold += quantity
      } else if (normStatus === "RETURNED") {
        existingSku.unitsReturned += quantity
      }
      existingSku.profit = existingSku.settlement - existingSku.purchaseCost
      skuMetricsMap.set(skuKey, existingSku)
    }
  }

  const skuReports: SkuReportItem[] = Array.from(skuMetricsMap.values())
    .map(item => {
      let category: "HIGH" | "MEDIUM" | "LOW" | "LOSS" = "LOW"
      if (item.profit <= 0) {
        category = "LOSS"
      } else if (item.profit > 2000) {
        category = "HIGH"
      } else if (item.profit >= 500) {
        category = "MEDIUM"
      }
      return {
        ...item,
        category
      }
    })
    .sort((a, b) => b.profit - a.profit)

  const reportId = `MC-${Math.floor(1000 + Math.random() * 9000)}`

  return {
    periodLabel: monthLabel,
    reportId,
    totalOrders: csvOrders.length,
    totalUnits,
    totalSettlement,
    totalPurchaseCost,
    totalAdsCost,
    totalReferral,
    netProfit: totalSettlement + totalReferral - totalAdsCost - totalPurchaseCost,
    deliveredSplit,
    undeliveredSplit,
    skuReports,
    orders: combinedOrders,
    files: []
  }
}

/** Extract a "Mon YYYY" month label from a Meesho-format date string (ISO, DD-MM-YYYY, or YYYY/MM). */
export function getMeeshoMonthLabel(dateStr: string): string | null {
  if (!dateStr) return null
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  let match = dateStr.match(/^(\d{4})-(\d{2})/)
  if (match) {
    const monthIndex = parseInt(match[2], 10) - 1
    if (months[monthIndex]) return `${months[monthIndex]} ${match[1]}`
  }
  match = dateStr.match(/(\d{2})[-/](\d{2})[-/](\d{4})/)
  if (match) {
    const monthIndex = parseInt(match[2], 10) - 1
    if (months[monthIndex]) return `${months[monthIndex]} ${match[3]}`
  }
  match = dateStr.match(/^(\d{4})\/(\d{2})/)
  if (match) {
    const monthIndex = parseInt(match[2], 10) - 1
    if (months[monthIndex]) return `${months[monthIndex]} ${match[1]}`
  }
  return null
}

export function computeCombinedReport(
  csvOrders: CsvOrderRow[],
  skuCosts: Record<string, SkuCostConfig>,
  paymentFiles: MeeshoPaymentFile[]
): Record<string, MeeshoCombinedReport> {
  const getMonthLabel = getMeeshoMonthLabel

  // 1. De-duplicate CSV Orders
  const csvMap = new Map<string, CsvOrderRow>()
  for (const o of csvOrders || []) {
    if (o.subOrderNo) {
      csvMap.set(o.subOrderNo, o)
    }
  }
  const uniqueCsvOrders = Array.from(csvMap.values())

  // 2. Identify unique months
  const monthsSet = new Set<string>()
  for (const o of uniqueCsvOrders) {
    const label = getMonthLabel(o.orderDate)
    if (label) {
      monthsSet.add(label)
    }
  }

  if (monthsSet.size === 0) {
    monthsSet.add("May 2026")
  }

  const reports: Record<string, MeeshoCombinedReport> = {}

  for (const monthLabel of monthsSet) {
    const monthCsv = uniqueCsvOrders.filter(o => getMonthLabel(o.orderDate) === monthLabel)

    const monthPaymentOrders: OrderRow[] = []
    for (const file of paymentFiles || []) {
      for (const order of file.orders || []) {
        if (getMonthLabel(order.orderDate) === monthLabel) {
          monthPaymentOrders.push(order)
        }
      }
    }

    const monthAds: AdRow[] = []
    for (const file of paymentFiles || []) {
      for (const ad of file.ads || []) {
        if (getMonthLabel(ad.deductionDuration) === monthLabel) {
          monthAds.push(ad)
        }
      }
    }

    const monthReferrals: ReferralRow[] = []
    for (const file of paymentFiles || []) {
      for (const ref of file.referrals || []) {
        if (getMonthLabel(ref.paymentDate) === monthLabel) {
          monthReferrals.push(ref)
        }
      }
    }

    const monthCompensations: CompensationRow[] = []
    for (const file of paymentFiles || []) {
      for (const comp of file.compensations || []) {
        if (getMonthLabel(comp.date) === monthLabel) {
          monthCompensations.push(comp)
        }
      }
    }

    reports[monthLabel] = computeSingleMonthReport(
      monthLabel,
      monthCsv,
      monthPaymentOrders,
      monthAds,
      monthReferrals,
      monthCompensations,
      skuCosts
    )
  }

  return reports
}
